/**
 * Session rune store — Phase 4-2 / T10.
 *
 * Holds the post-SSO session state for the running widget instance. Reactive
 * via Svelte 5 `$state` — components access fields as `session.state.ready`
 * etc.
 *
 * Importers: src/App.svelte (reads session.state to gate UI),
 * src/lib/embed/bootstrap.ts (calls setReady / setError after SSO).
 */

import type { SsoResponse } from '../api/queries.js';

export interface SessionState {
  ready: boolean;
  sso: SsoResponse | null;
  error: string | null;
}

const STORAGE_KEY = 'widget.session.v1';

function readStorage(): SsoResponse | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SsoResponse;
  } catch {
    return null;
  }
}

function writeStorage(sso: SsoResponse | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (sso) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sso));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* quota / privacy mode — best effort */
  }
}

class SessionStore {
  state = $state<SessionState>({ ready: false, sso: null, error: null });

  hydrate(): SsoResponse | null {
    const sso = readStorage();
    if (sso) {
      this.state.ready = true;
      this.state.sso = sso;
      this.state.error = null;
    }
    return sso;
  }

  setReady(sso: SsoResponse): void {
    this.state.ready = true;
    this.state.sso = sso;
    this.state.error = null;
    writeStorage(sso);
  }

  setError(error: string): void {
    this.state.error = error;
    this.state.ready = false;
  }

  reset(): void {
    this.state.ready = false;
    this.state.sso = null;
    this.state.error = null;
    writeStorage(null);
  }
}

export const session = new SessionStore();
